/**
 * Typed dot-path lookups from a schema enum value to its French message key
 * (`docs/CONVENTIONS.md` §1: every visible string goes through `t()`, and
 * `MessageKey` makes a typo a compile error rather than a blank string).
 */
import type { components } from '../../api/schema';
import type { MessageKey } from '../../i18n/fr';
import type {
  CaseStage,
  EngagementKind,
  FollowupStatus,
  FollowupStep,
  TurnoutStatus,
  VisitOutcome,
} from '../../api/types';

/** `docs/API.md` §7's `close_reason` enum, as `RegistrationCaseAdvanceRequest` accepts it. */
export type CloseReason = components['schemas']['CloseReasonEnum'];

/** `voted` / `abstained` / `unknown` → the written status a household badge shows. */
export const TURNOUT_LABEL_KEYS: Record<TurnoutStatus, MessageKey> = {
  voted: 'household.sheet.turnout.voted',
  abstained: 'household.sheet.turnout.abstained',
  unknown: 'household.sheet.turnout.unknown',
};

/** A visit outcome → its label on the "Résultat de la visite" grid. */
export const OUTCOME_LABEL_KEYS: Record<VisitOutcome, MessageKey> = {
  absent: 'household.outcomes.absent',
  exchange: 'household.outcomes.exchange',
  refusal: 'household.outcomes.refusal',
  come_back: 'household.outcomes.come_back',
  registration_info: 'household.outcomes.registration_info',
  moved: 'household.outcomes.moved',
  phantom_absent_unit: 'household.outcomes.phantom_absent_unit',
};

/** A star count (1-5) → the written description under `StarRating`. */
export const STAR_LABEL_KEYS: Record<number, MessageKey> = {
  1: 'household.stars.label1',
  2: 'household.stars.label2',
  3: 'household.stars.label3',
  4: 'household.stars.label4',
  5: 'household.stars.label5',
};

/** A registration case stage → its label on the stage bar and case card. */
export const CASE_STAGE_LABEL_KEYS: Record<CaseStage, MessageKey> = {
  info_given: 'household.cases.stage.info_given',
  filed: 'household.cases.stage.filed',
  confirmed: 'household.cases.stage.confirmed',
  closed: 'household.cases.stage.closed',
};

/** A follow-up message's status → its badge label. */
export const MESSAGE_STATUS_LABEL_KEYS: Record<FollowupStatus, MessageKey> = {
  scheduled: 'household.cases.messageStatus.scheduled',
  sent: 'household.cases.messageStatus.sent',
  opened: 'household.cases.messageStatus.opened',
  replied: 'household.cases.messageStatus.replied',
  unsubscribed: 'household.cases.messageStatus.unsubscribed',
  failed: 'household.cases.messageStatus.failed',
  cancelled: 'household.cases.messageStatus.cancelled',
};

/** A follow-up step (`j2`/`j7`/`j14`) → its short label ("J+2"). */
export const STEP_LABEL_KEYS: Record<FollowupStep, MessageKey> = {
  j2: 'household.cases.step.j2',
  j7: 'household.cases.step.j7',
  j14: 'household.cases.step.j14',
};

/** An engagement kind → its checkbox label on the household sheet. */
export const ENGAGEMENT_LABEL_KEYS: Record<EngagementKind, MessageKey> = {
  contact: 'household.engagements.contact.label',
  newsletter: 'household.engagements.newsletter.label',
  support_campaign: 'household.engagements.support_campaign.label',
  building_referent: 'household.engagements.building_referent.label',
  join_group: 'household.engagements.join_group.label',
};

/** An engagement kind's muted explanation, for every kind but `contact` (which has fields instead). */
export const ENGAGEMENT_HINT_KEYS: Partial<Record<EngagementKind, MessageKey>> = {
  newsletter: 'household.engagements.newsletter.hint',
  support_campaign: 'household.engagements.support_campaign.hint',
  building_referent: 'household.engagements.building_referent.hint',
  join_group: 'household.engagements.join_group.hint',
};

/** A close reason → its option label in the "Clore" sheet. */
export const CLOSE_REASON_LABEL_KEYS: Record<CloseReason, MessageKey> = {
  confirmed: 'household.cases.closeReason.confirmed',
  unsubscribed: 'household.cases.closeReason.unsubscribed',
  no_answer: 'household.cases.closeReason.no_answer',
  withdrawn: 'household.cases.closeReason.withdrawn',
  other: 'household.cases.closeReason.other',
};
